import {
  Bot, Building2, Clapperboard, Coins, DoorOpen, Gem, Globe2, Mail, Mailbox, Palette, Plug, Search, Send, Settings, Sparkles, UserPlus, Wallet,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

/**
 * Community Settings, one page per topic (UX review 2026-10-27, in the way
 * Circle, Google Admin and Slack organize theirs): a left nav grouped by
 * intent, each topic at `/settings/{slug}` and General at `/settings`.
 * Title, description and search keywords are `settings:menu.sections.{slug}.*`.
 *
 * The page is owner/admin only (everyone else goes to My Account, as before);
 * `roles` narrows a topic further. A topic nobody here can use is left out of
 * the nav, so no page is ever empty.
 */
export type SettingsSlug =
  | 'general' | 'branding' | 'domain' | 'access' | 'welcome' | 'features' | 'visibility' | 'coworking'
  | 'emails' | 'sending' | 'integrations' | 'video' | 'assistants'
  | 'plan' | 'credits' | 'business' | 'payout'

export type SettingsGroup = 'community' | 'communication' | 'connections' | 'business'

export interface SettingsSectionDef {
  slug: SettingsSlug
  group: SettingsGroup
  icon: LucideIcon
  roles: string[]
}

const MANAGERS = ['owner', 'admin']

export const SETTINGS_GROUPS: SettingsGroup[] = ['community', 'communication', 'connections', 'business']

export const SETTINGS_SECTIONS: SettingsSectionDef[] = [
  { slug: 'general', group: 'community', icon: Settings, roles: MANAGERS },
  { slug: 'branding', group: 'community', icon: Palette, roles: MANAGERS },
  { slug: 'domain', group: 'community', icon: Globe2, roles: MANAGERS },
  { slug: 'access', group: 'community', icon: UserPlus, roles: MANAGERS },
  /* F-155 + F-190: the welcome on the first access and the "Primeiros passos". */
  { slug: 'welcome', group: 'community', icon: DoorOpen, roles: MANAGERS },
  { slug: 'features', group: 'community', icon: Sparkles, roles: MANAGERS },
  { slug: 'visibility', group: 'community', icon: Search, roles: MANAGERS },
  /* F-245: the Coworking extension's settings (virtual office: business address and the plans that include it). */
  { slug: 'coworking', group: 'community', icon: Mailbox, roles: MANAGERS },
  { slug: 'emails', group: 'communication', icon: Mail, roles: MANAGERS },
  /* F-188: e-mail through the community's own Resend account (Business and above). */
  { slug: 'sending', group: 'communication', icon: Send, roles: MANAGERS },
  { slug: 'integrations', group: 'connections', icon: Plug, roles: MANAGERS },
  /* F-197b: the keys that sign Bunny Stream and Cloudflare Stream players. */
  { slug: 'video', group: 'connections', icon: Clapperboard, roles: MANAGERS },
  { slug: 'assistants', group: 'connections', icon: Bot, roles: MANAGERS },
  { slug: 'plan', group: 'business', icon: Gem, roles: MANAGERS },
  { slug: 'credits', group: 'business', icon: Coins, roles: MANAGERS },
  { slug: 'business', group: 'business', icon: Building2, roles: MANAGERS },
  { slug: 'payout', group: 'business', icon: Wallet, roles: MANAGERS },
]

export const SETTINGS_ROOT = '/settings'

/** `/settings` and `/settings/{anything}` (query and hash aside). */
export const isSettingsPath = (path: string): boolean => {
  const route = path.split(/[?#]/)[0]
  return route === SETTINGS_ROOT || route.startsWith(`${SETTINGS_ROOT}/`)
}

export const settingsPathFor = (slug: SettingsSlug): string =>
  slug === 'general' ? SETTINGS_ROOT : `${SETTINGS_ROOT}/${slug}`

/** The slug a path asks for: '' for `/settings`, the next segment otherwise. */
export const askedSlugOf = (path: string): string =>
  path.split(/[?#]/)[0].slice(SETTINGS_ROOT.length).split('/').filter(Boolean)[0] ?? ''

/**
 * The old tabs (`/settings?tab=business`, the e-mails' `?tab=payout`, the
 * integrations notices' `?tab=integrations&sub=apps`) and the section each
 * one became. Kept so old links and bookmarks land on the same content.
 */
export const LEGACY_TABS: Record<string, SettingsSlug> = {
  general: 'general',
  plan: 'plan',
  business: 'business',
  payout: 'payout',
  credits: 'credits',
  assistants: 'assistants',
  integrations: 'integrations',
}
